export type ReportType = 'execution-summary' | 'filtered' | 'comparison' | 'trend' | 'failure-analysis';
export type ExportFormat = 'pdf' | 'excel' | 'html' | 'pdf-excel' | 'all';
export type DetailLevel = 'scenario' | 'step' | 'functional-area' | 'test-type';
export type ReportStatus = 'generating' | 'completed' | 'failed';
export type DateRangeMode = 'fixed' | 'last-7-days' | 'last-14-days' | 'last-30-days' | 'this-week' | 'this-month' | 'this-quarter';
export type ComparisonSelectType = 'execution-run' | 'tenant-type';

export const COMPARISON_SELECT_TYPE_OPTIONS: { label: string; value: ComparisonSelectType }[] = [
    { label: 'Execution Run', value: 'execution-run' },
    { label: 'Tenant Type', value: 'tenant-type' }
];

export interface ReportTypeOption {
    id: ReportType;
    label: string;
    description: string;
    icon: string;
    disabled?: boolean;
}

export interface ExecutionRunOption {
    id: string;
    runId: string;
    executedOn: string;
    packageName: string;
    scenarioCount: number;
    passRate: number;
    executedByName: string;
    clientId: string | null;
}

export interface ReportGeneratorConfig {
    reportType: ReportType | null;
    clientId: string | null;
    executionRunId: string | null;
    comparisonRunId: string | null;
    comparisonSelectType: ComparisonSelectType | null;
    baselineTenantId: string | null;
    comparisonTenantId: string | null;
    packageId: string | null;
    functionalAreaIds: string[];
    testTypeId: string | null;
    statusFilter: string | null;
    dateRangeMode: DateRangeMode;
    dateRange: Date[] | null;
    detailLevel: DetailLevel | null;
    exportFormat: ExportFormat | null;
    includeScreenshots: boolean;
    includeErrorLogs: boolean;
    includeStepDurations: boolean;
}

export interface GeneratedReport {
    id: string;
    reportName: string;
    reportType: ReportType;
    generatedBy: string;
    generatedOn: string;
    exportFormat: ExportFormat;
    status: ReportStatus;
    fileUrl: string | null;
    config: ReportGeneratorConfig;
}

export interface SavedReport {
    id: string;
    reportName: string;
    reportType: ReportType;
    detailLevel: DetailLevel | null;
    exportFormat: ExportFormat | null;
    generatedBy: string | null;
    generatedByUser?: { id: string; firstName: string; lastName: string } | null;
    generatedOn: string;
    passRate: number;
    totalScenarios: number;
    totalPassed: number;
    totalFailed: number;
    config: ReportGeneratorConfig;
    previewSummary: ReportPreviewSummary;
    previewScenarios: ReportPreviewScenario[];
    functionalAreaBreakdown: { name: string; scenarios: number; totalSteps: number; passed: number; failed: number; stopped: number; passRate: number }[];
    testTypeBreakdown: { name: string; scenarios: number; totalSteps: number; passed: number; failed: number; stopped: number; passRate: number }[];
}

export interface ReportPreviewSummary {
    totalScenarios: number;
    totalSteps: number;
    totalPassed: number;
    totalFailed: number;
    totalStopped: number;
    passRate: number;
}

export type StabilityTag = 'stable' | 'improving' | 'regressing' | 'flaky' | 'new';
export type FailureSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface ReportPreviewScenario {
    scenarioId: string;
    scenarioName: string;
    functionalAreaName: string;
    totalSteps: number;
    passed: number;
    failed: number;
    duration: string;
    status: string;
    steps?: ReportPreviewStep[];
    packageName?: string;
    testTypeName?: string;
    executionId?: string;
    executedOn?: string;
    passRate?: number;
    // Trend fields
    prevPassRate?: number;
    delta?: number;
    stabilityTag?: StabilityTag;
    // Failure Analysis fields
    errorCategory?: string;
    failureCount?: number;
    totalRunsAnalyzed?: number;
    firstFailedDate?: string;
    lastFailedDate?: string;
    rootCause?: string;
    severity?: FailureSeverity;
}

export interface TrendSummary {
    runsAnalyzed: number;
    avgPassRate: number;
    trendDirection: number;
    flakyTests: number;
    regressions: number;
}

export interface FailureAnalysisSummary {
    totalFailed: number;
    uniqueFailures: number;
    recurring: number;
    mostImpactedArea: string;
    avgFailureRate: number;
}

export interface ReportPreviewStep {
    stepOrder: number;
    stepDescription: string;
    status: string;
    duration: string;
    error: string | null;
    screenshotUrl: string | null;
}

export interface ComparisonDelta {
    scenarioId: string;
    scenarioName: string;
    functionalAreaName: string;
    changeType: 'fixed' | 'regression' | 'new-fail' | 'stable-pass' | 'stable-fail';
    beforeStatus: string;
    afterStatus: string;
    details: string;
}

export interface ComparisonSummary {
    nowPassing: number;
    newFailures: number;
    regressions: number;
    passRateDelta: number;
    beforePassRate: number;
    afterPassRate: number;
}

export const REPORT_TYPE_OPTIONS: ReportTypeOption[] = [
    {
        id: 'execution-summary',
        label: 'Execution Summary',
        description: 'Single execution run overview with all scenario results',
        icon: 'pi pi-chart-bar'
    },
    {
        id: 'filtered',
        label: 'Custom Report',
        description: 'Custom query: by package, functional area, date range, status',
        icon: 'pi pi-sliders-h'
    },
    {
        id: 'comparison',
        label: 'Comparison Report',
        description: 'Side-by-side diff of two execution runs',
        icon: 'pi pi-arrows-h'
    },
    {
        id: 'trend',
        label: 'Trend Report',
        description: 'Pass rate trends over time, flaky test detection, regression tracking',
        icon: 'pi pi-chart-line'
    },
    {
        id: 'failure-analysis',
        label: 'Failure Analysis Report',
        description: 'Recurring failures, root cause grouping, and impacted areas',
        icon: 'pi pi-exclamation-triangle'
    }
];

export const EXPORT_FORMAT_OPTIONS = [
    { label: 'PDF', value: 'pdf' as ExportFormat },
    { label: 'Excel', value: 'excel' as ExportFormat },
    { label: 'HTML', value: 'html' as ExportFormat }
];

export const DETAIL_LEVEL_OPTIONS = [
    { label: 'Scenario Level (Summary)', value: 'scenario' as DetailLevel },
    { label: 'Step Level (Detailed)', value: 'step' as DetailLevel },
    { label: 'Functional Area Level', value: 'functional-area' as DetailLevel },
    { label: 'Test Type Level', value: 'test-type' as DetailLevel }
];

export const STATUS_FILTER_OPTIONS = [
    { label: 'All Statuses', value: 'all' },
    { label: 'Passed', value: 'Passed' },
    { label: 'Failed', value: 'Failed' },
    { label: 'Stopped', value: 'Stopped' }
];

export const DATE_RANGE_MODE_OPTIONS: { label: string; value: DateRangeMode }[] = [
    { label: 'Custom Range', value: 'fixed' },
    { label: 'Last 7 Days', value: 'last-7-days' },
    { label: 'Last 14 Days', value: 'last-14-days' },
    { label: 'Last 30 Days', value: 'last-30-days' },
    { label: 'This Week', value: 'this-week' },
    { label: 'This Month', value: 'this-month' },
    { label: 'This Quarter', value: 'this-quarter' }
];
